import { useMutation } from '@apollo/client';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { useFormik } from 'formik';
import moment from 'moment';
import 'moment/min/locales';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffectOnce } from 'react-use';
import StyledMarkdown from 'src/QuotationWidget/StyledMarkdown';
import { InferType, object, string } from 'yup';
import { useQuotationContext } from '../contexts/QuotationContext';
import { Promotion, PROMOTION_VALIDATION } from '../graphql';
import { CHECK_GREEN } from '../helpers/colors';
import BareTextField from './BareTextField';

type Props = {
  couponCode: string;
};

const schema = () =>
  object({
    coupon_code: string().trim().uppercase().required(),
  });

type Schema = InferType<ReturnType<typeof schema>>;

const CouponForm = ({ couponCode }: Props) => {
  const intl = useIntl();
  const {
    coupon,
    setCoupon,
    values: { age },
    validateIssueAge,
    activeProduct,
  } = useQuotationContext();
  const [validate] =
    useMutation<{ promotion: Promotion }>(PROMOTION_VALIDATION);
  const [isInvalidCode, setIsInvalidCode] = useState(false);

  const onSubmit = useCallback(
    async (values: Schema) => {
      const { data } = await validate({
        variables: {
          productId: activeProduct?.code,
          promoCode: values.coupon_code,
        },
      });
      if (data?.promotion) {
        setCoupon(data.promotion);
      } else {
        setCoupon(undefined);
        setIsInvalidCode(true);
      }
    },
    [activeProduct?.code, setCoupon, validate],
  );

  const {
    values,
    isSubmitting,
    isValid,
    submitForm,
    handleSubmit,
    setFieldValue,
  } = useFormik<Schema>({
    initialValues: { coupon_code: couponCode },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: schema,
    onSubmit,
  });

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCoupon(undefined);
      setIsInvalidCode(false);
      setFieldValue('coupon_code', event.target.value?.toUpperCase() || '');
    },
    [setCoupon, setFieldValue],
  );

  const disabledField = useMemo(() => {
    const isIssueAgeValid = validateIssueAge(activeProduct, age);
    return !isIssueAgeValid || isSubmitting;
  }, [activeProduct, age, isSubmitting, validateIssueAge]);

  const disabledSubmit = useMemo(() => {
    return disabledField || !isValid || isSubmitting;
  }, [disabledField, isSubmitting, isValid]);

  const helperText = useMemo(() => {
    if (coupon) {
      return (
        <span style={{ color: CHECK_GREEN }}>
          <FormattedMessage
            id="coupon.message"
            values={{
              date: moment(coupon.effective_to)
                .locale(intl.locale)
                .format('LL'),
              message: <StyledMarkdown>{coupon.message}</StyledMarkdown>,
            }}
          />
        </span>
      );
    } else if (isInvalidCode) {
      return intl.formatMessage({ id: 'coupon.not_found' });
    } else {
      return '';
    }
  }, [coupon, intl, isInvalidCode]);

  useEffectOnce(() => {
    // `submitForm` will validate form before submitting, API call is
    // avoided if no default coupon code is provided.
    submitForm();
  });

  return (
    <form action="#" onSubmit={handleSubmit}>
      <Paper style={{ boxShadow: '0px 0px 6px 0px rgba(0,0,0,0.35)' }}>
        <BareTextField
          name="coupon_code"
          label={<FormattedMessage id="coupon.coupon_code" />}
          value={values.coupon_code}
          disabled={disabledField}
          error={isInvalidCode}
          helperText={helperText}
          onChange={onChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  aria-label="redeem coupon code"
                  type="submit"
                  disabled={disabledSubmit}
                  color="primary"
                >
                  <FormattedMessage id="coupon.redeem" />
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </Paper>
    </form>
  );
};

export default CouponForm;
